import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import {
  ANY_LINK_DYNAMIC_TOOLTIP,
  DynamicContentChip,
  SPECIFIC_LINK_DYNAMIC_TOOLTIP,
} from "./dynamic-content-chip";

import type { ChangeEvent, FC } from "react";

const ANY_LINK_VALUE = "__any__";

interface FollowUpLinkSelectDialogProps {
  open: boolean;
  hyperlinks: string[];
  value: string | null | undefined;
  onSelect: (url: string | null) => void;
  onCancel: () => void;
  dynamicOnlyLinks?: Set<string>;
}

function toRadioValue(value: string | null | undefined): string | undefined {
  if (value === null) return ANY_LINK_VALUE;
  if (value === undefined) return undefined;
  return value;
}

const FollowUpLinkSelectDialogContent: FC<{
  hyperlinks: string[];
  initialValue: string | null | undefined;
  onSelect: (url: string | null) => void;
  onCancel: () => void;
  dynamicOnlyLinks: Set<string>;
}> = ({ hyperlinks, initialValue, onSelect, onCancel, dynamicOnlyLinks }) => {
  const [selected, setSelected] = useState<string | undefined>(toRadioValue(initialValue));

  const handleChange = useCallback((_: ChangeEvent<HTMLInputElement>, val: string): void => {
    setSelected(val);
  }, []);

  const handleSelect = useCallback((): void => {
    if (selected === undefined) return;
    onSelect(selected === ANY_LINK_VALUE ? null : selected);
  }, [selected, onSelect]);

  return (
    <>
      <DialogTitle>Select link/button for follow up</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Typography variant="body2">
            You can check for the correct link by using the &quot;View&quot; button (link will open
            in a new tab).
          </Typography>
          <RadioGroup
            name="follow-up-link-select-group"
            value={selected ?? ""}
            onChange={handleChange}
          >
            <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
              <FormControlLabel
                value={ANY_LINK_VALUE}
                control={<Radio size="small" />}
                label={
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Typography variant="body2">Any link in this Communication</Typography>
                    {dynamicOnlyLinks.size > 0 && (
                      <DynamicContentChip tooltipText={ANY_LINK_DYNAMIC_TOOLTIP} />
                    )}
                  </Stack>
                }
              />
              {hyperlinks.map((url) => (
                <Stack
                  key={url}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center", width: "100%", minWidth: 0 }}
                >
                  <FormControlLabel
                    value={url}
                    control={<Radio size="small" />}
                    label={
                      <Tooltip title={url} placement="top-start">
                        <Typography
                          variant="body2"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            wordBreak: "break-all",
                          }}
                        >
                          {url}
                        </Typography>
                      </Tooltip>
                    }
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      "& .MuiFormControlLabel-label": {
                        minWidth: 0,
                        overflow: "hidden",
                        display: "block",
                      },
                    }}
                  />
                  {dynamicOnlyLinks.has(url) && (
                    <DynamicContentChip tooltipText={SPECIFIC_LINK_DYNAMIC_TOOLTIP} />
                  )}
                  <Button
                    data-analytics-id="follow-up-link-select-view-button"
                    variant="text"
                    size="small"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    startIcon={<OpenInNew fontSize="small" />}
                    sx={{ flexShrink: 0, fontWeight: 600 }}
                  >
                    View
                  </Button>
                </Stack>
              ))}
            </Stack>
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
        <Button
          data-analytics-id="follow-up-link-select-cancel-button"
          variant="text"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-analytics-id="follow-up-link-select-confirm-button"
          variant="contained"
          onClick={handleSelect}
          disabled={selected === undefined}
        >
          Select
        </Button>
      </DialogActions>
    </>
  );
};

export const FollowUpLinkSelectDialog: FC<FollowUpLinkSelectDialogProps> = ({
  open,
  hyperlinks,
  value,
  onSelect,
  onCancel,
  dynamicOnlyLinks,
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
    {open && (
      <FollowUpLinkSelectDialogContent
        hyperlinks={hyperlinks}
        initialValue={value}
        onSelect={onSelect}
        onCancel={onCancel}
        dynamicOnlyLinks={dynamicOnlyLinks ?? new Set()}
      />
    )}
  </Dialog>
);
